/**
 * ExpandableMarkdown — collapsed by default, renders user text as a safe
 * subset of Markdown when expanded.
 *
 * No `dangerouslySetInnerHTML`, no runtime HTML parsing. Inline markers are
 * split with a regex and the captured groups are rendered through React
 * elements — so a pasted `<script>` stays inert text. Links only render as
 * anchors when the href is http / https; anything else (javascript:,
 * data:, etc.) falls back to plain text.
 *
 * Supported markdown:
 *   - `**bold**`
 *   - `*italic*`
 *   - `` `inline code` ``
 *   - `[text](https://…)`
 *   - Headings `# ## ###`
 *   - Bulleted list (`- ` or `* ` at line start)
 *   - Numbered list (`1. `)
 *   - Paragraph breaks (blank line)
 *   - Hard wrap (single newline inside a paragraph)
 *
 * Collapsed preview uses a plain-text projection (markers stripped, no
 * partial **bold**) so truncation never cuts a marker mid-token.
 */
import React, { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ExpandableMarkdownProps {
  text: string;
  previewChars?: number;
  className?: string;
}

const INLINE_PATTERN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\n]+\))/g;

function Inline({ text }: { text: string }): React.ReactElement {
  const parts = text.split(INLINE_PATTERN);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        const b = /^\*\*([^*]+)\*\*$/.exec(part);
        if (b) return <strong key={i}>{b[1]}</strong>;
        const it = /^\*([^*]+)\*$/.exec(part);
        if (it) return <em key={i}>{it[1]}</em>;
        const c = /^`([^`]+)`$/.exec(part);
        if (c) {
          return (
            <code
              key={i}
              className="px-1 py-0.5 rounded bg-[var(--surface-raised)] border border-[var(--border-subtle)] text-xs font-mono text-[var(--text-primary)]"
            >
              {c[1]}
            </code>
          );
        }
        const l = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
        if (l) {
          const href = l[2];
          // Reject anything but http(s) so a paste of `[x](javascript:…)`
          // degrades to plain text instead of producing a live anchor.
          if (!/^https?:\/\//i.test(href)) {
            return <span key={i}>{part}</span>;
          }
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--accent-primary)] hover:underline break-all"
            >
              {l[1]}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function Block({ text }: { text: string }): React.ReactElement | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const headingMatch = /^(#{1,3})\s+(.+)$/.exec(trimmed);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const content = headingMatch[2];
    const sizeClass =
      level === 1 ? 'text-lg font-semibold' :
      level === 2 ? 'text-base font-semibold' :
      'text-sm font-semibold';
    return (
      <div className={`${sizeClass} text-[var(--text-primary)]`}>
        <Inline text={content} />
      </div>
    );
  }

  const lines = trimmed.split('\n').map((l) => l.trimEnd()).filter(Boolean);
  if (lines.length > 0 && lines.every((l) => /^\s*[-*]\s+/.test(l))) {
    return (
      <ul className="list-disc list-outside pl-5 space-y-1">
        {lines.map((l, i) => (
          <li key={i}><Inline text={l.replace(/^\s*[-*]\s+/, '')} /></li>
        ))}
      </ul>
    );
  }
  if (lines.length > 0 && lines.every((l) => /^\s*\d+\.\s+/.test(l))) {
    return (
      <ol className="list-decimal list-outside pl-5 space-y-1">
        {lines.map((l, i) => (
          <li key={i}><Inline text={l.replace(/^\s*\d+\.\s+/, '')} /></li>
        ))}
      </ol>
    );
  }

  // Paragraph — single \n within the block renders as an inline <br />.
  const subLines = trimmed.split('\n');
  const children: ReactNode[] = [];
  subLines.forEach((l, i) => {
    if (i > 0) children.push(<br key={`br-${i}`} />);
    children.push(<Inline key={`ln-${i}`} text={l} />);
  });
  return <p className="leading-relaxed">{children}</p>;
}

export function Markdown({ text }: { text: string }): React.ReactElement {
  const blocks = text.split(/\n\s*\n/);
  return (
    <div className="space-y-2 break-words">
      {blocks.map((b, i) => <Block key={i} text={b} />)}
    </div>
  );
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,3}\s+/gm, '')
    .replace(/^\s*[-*]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .trim();
}

export function ExpandableMarkdown({
  text,
  previewChars = 220,
  className = '',
}: ExpandableMarkdownProps): React.ReactElement | null {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const plain = stripMarkdown(text);
  const needsToggle = plain.length > previewChars;

  if (!needsToggle) {
    return (
      <div className={className}>
        <Markdown text={text} />
      </div>
    );
  }

  if (!expanded) {
    // Cut at the last space before previewChars so we don't slice a word.
    const sliced = plain.slice(0, previewChars);
    const preview = sliced.replace(/\s+\S*$/, '').trimEnd() + '…';
    return (
      <div className={className}>
        <p className="leading-relaxed whitespace-pre-wrap">{preview}</p>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 text-sm font-medium text-[var(--accent-primary)] hover:underline"
        >
          {t('common.showMore', 'Show more')}
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <Markdown text={text} />
      <button
        type="button"
        onClick={() => setExpanded(false)}
        className="mt-1 text-sm font-medium text-[var(--accent-primary)] hover:underline"
      >
        {t('common.showLess', 'Show less')}
      </button>
    </div>
  );
}

export default ExpandableMarkdown;
